import { cn } from '@/lib/utils';

export type BrandVariant = 'green' | 'cream' | 'ink';

export interface BrandWordmarkProps {
  /** font-size of the Arabic «وصّل» in px (the unit everything scales from) */
  size?: number;
  /** color treatment — green (default) · cream (on green/ink) · ink (mono) */
  variant?: BrandVariant;
  /** show the «Wassel» Latin counterpart (true) or Arabic only (false) */
  showEn?: boolean;
  className?: string;
}

const COLOR: Record<BrandVariant, string> = {
  green: 'var(--green)',
  cream: 'var(--cream)',
  ink: 'var(--ink)',
};

/**
 * Wassel wordmark — the locked "Option 11" bilingual logotype.
 * «وصّل» (Thmanyah Display Black, leads) + «Wassel» (Thmanyah Display Medium,
 * ~0.45× the Arabic, lifted +0.10X on a shared baseline). The shadda on «وصّل»
 * is mandatory and is part of the string — never strip it.
 *
 * This is a wordmark (live Thmanyah Display text), NOT a symbol. The radar /
 * circle as a logo is explicitly rejected by the brand; the radar stays a
 * product feature only. The bespoke vector glyph refinement (§2.6) is deferred —
 * the Thmanyah base is the approved ~95%.
 */
export default function BrandWordmark({
  size = 28,
  variant = 'green',
  showEn = true,
  className,
}: BrandWordmarkProps) {
  const color = COLOR[variant];
  return (
    <span
      dir="rtl"
      role="img"
      aria-label="وصّل Wassel"
      className={cn('inline-flex items-baseline select-none', className)}
      style={{ color, lineHeight: 1, fontFamily: '"Thmanyah Display", "Thmanyah Sans", system-ui, sans-serif' }}
    >
      <span style={{ fontWeight: 900, fontSize: size, lineHeight: 1, letterSpacing: '-0.005em' }}>
        {'وصّل'}
      </span>
      {showEn && (
        <span
          aria-hidden
          style={{
            fontWeight: 500,
            fontSize: Math.round(size * 0.45),
            lineHeight: 1,
            marginInlineStart: Math.round(size * 0.34),
            position: 'relative',
            top: -Math.round(size * 0.1),
          }}
        >
          Wassel
        </span>
      )}
    </span>
  );
}
